<template>
    <b-modal
        v-model="open"
        size="md"
        centered
        hide-header
        hide-footer
        no-close-on-esc
        button-size="sm"
        no-close-on-backdrop
        :busy="isSubmitting"
        :ok-disabled="isSubmitting"
    >
        <h5>{{ modalTitle }}</h5>
        <b-form-group
            :label="$t('values.selectTheEvaluators')"
            label-for="select-user"
        >
            <users
                id="select-user"
                multiple
                allow-empty
                close-on-select
                clear-on-select
                :options="options"
                :state="selectState"
                :loading="isLoading"
                aria-describedby="users"
                :internal-search="false"
                :selected="selectedUsers"
                :next-page-button="nextPageOptions"
                :placeholder="$t('values.chooseAuserOrTypeForMoreResults')"
                :no-elements-found="$t('form.multiselect.noUserWasFound')"
                @changeValue="setUser"
                @nextPage="onClickNextPage"
                @changeSearch="debouncedSearchHandler"
            />
            <b-form-invalid-feedback id="users">
                <span v-if="!$v.selectedUsers.required">
                    {{ $t('form.validations.users_required') }}
                </span>
            </b-form-invalid-feedback>
        </b-form-group>
        <div class="float-right">
            <b-button size="sm" class="mr-2" @click="$emit('cancel')">
                {{ $t('form.button.cancel') }}
            </b-button>
            <b-button size="sm" variant="primary" @click="onClickCreateNetwork">
                {{ $t('form.button.save') }}
            </b-button>
        </div>
    </b-modal>
</template>
<script>
import i18n from '@/i18n'
import Modal from '@/views/components/modal'
import SelectWithAvatar from '@/views/components/selects/select-with-avatar'
import { debounce } from 'lodash'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { proceedWithConfirmation } from '@/lib/sweet-alert'
import { createNetworks, getUsers } from '@/views/values/api'
import { mapperToOptions } from '@/views/components/selects/select-with-avatar'

export default {
    mixins: [validationMixin],
    props: {
        open: {
            type: Boolean,
            required: true,
        },
        user: {
            type: Object,
            required: true,
        },
        networkType: {
            type: String,
            required: true,
        },
    },
    components: {
        Modal,
        Users: SelectWithAvatar,
    },
    created() {
        this.debouncedSearchHandler = debounce(this.getUsers, 1000)
        this.setDefaultOptions()
    },
    validations: {
        selectedUsers: {
            required,
        },
    },
    data() {
        return {
            options: [],
            selectedUsers: [],
            isSubmitting: false,
            searchName: null,
            oldSearchName: null,
            paginaton: {
                page: 1,
                lastPage: 1,
                perPage: 50,
            },
            isLoading: false,
            useDefaultUsers: true,
        }
    },
    methods: {
        setUser(users) {
            this.$v.selectedUsers.$touch()
            this.selectedUsers = users
        },
        onClickCreateNetwork() {
            if (!this.validate()) {
                return
            }
            proceedWithConfirmation({
                onConfirm: () => {
                    return createNetworks({
                        status: 'request',
                        type: this.networkType,
                        evaluated_id: this.user.id,
                        valuers_ids: this.selectedUsers.map(i => i.value),
                    }).then(createdNetworks => {
                        this.$emit('networks-created', createdNetworks)
                        this.$emit('close')
                        this.selectedUsers = []
                        this.setDefaultOptions()
                        this.$v.$reset()
                    })
                },
            })
        },
        onClickNextPage() {
            this.paginaton.page++
            this.getUsers(this.searchName)
        },
        getDefaultUsers() {
            let users = []
            if (this.networkType == 'peer') {
                const manager = this.user.manager
                users = manager
                    ? manager.subordinates.filter(
                          user => user.id !== this.user.id
                      )
                    : []
            } else if (this.networkType == 'team') {
                users = this.user.subordinates
            } else if (this.networkType == 'leader') {
                const manager = this.user.manager
                users = manager ? [manager] : []
            }

            const valuersIds = this.user.valuers.map(i => i.id)
            return users.filter(user => {
                return !valuersIds.includes(user.id)
            })
        },
        setDefaultOptions() {
            const defaultUsers = this.getDefaultUsers()
            this.options = mapperToOptions(defaultUsers)
        },
        getUsers(searchName) {
            this.isLoading = true
            getUsers({
                and: 'yes',
                orderBy: 'name',
                user_id: this.user.id,
                scopesNames: ['not_valuer'],
                page: this.paginaton.page,
                per_page: this.paginaton.perPage,
                filter: 'id;name;occupation;picture',
                searchFields: 'id:!=;name:like;has_value:=',
                search:
                    `id:${this.user.id};` +
                    `name:${searchName};` +
                    `has_value:1`,
            }).then(data => {
                if (data.current_page > 1) {
                    this.options = this.options.concat(
                        mapperToOptions(data.data)
                    )
                } else {
                    this.options = mapperToOptions(data.data)
                }

                this.paginaton.lastPage = data.last_page
                this.isLoading = false
                this.searchName = searchName
            })
        },
        validate() {
            this.$v.$touch()
            return this.isValid
        },
    },
    computed: {
        modalTitle() {
            let type = this.networkType
            type = type.charAt(0).toUpperCase() + type.slice(1)
            return i18n.t('values.adding' + type)
        },
        selectState() {
            return this.$v.selectedUsers.$dirty
                ? !this.$v.selectedUsers.$invalid
                : null
        },
        isValid() {
            return !this.$v.selectedUsers.$anyError
        },
        nextPageOptions() {
            return {
                isShowNextPage: this.paginaton.lastPage > 1,
                isLastPage: this.paginaton.page == this.paginaton.lastPage,
                loading: this.isLoading,
            }
        },
    },
    watch: {
        networkType() {
            this.setDefaultOptions()
        },
    },
}
</script>
